// The virtual ECU. Everything here is a pure function of (profile, state, ...) ->
// { nextState, ... } — no React, no timers, no randomness leaks outside of what's
// explicitly passed in — so the UI layer owns all side effects (setInterval, RNG)
// and this file stays easy to reason about and reuse (e.g. from a future test file).
import { SESSIONS, SESSION_KEY_TO_ID } from './constants';
import { encodeDidValue, did16ToBytes } from './parser';
import { dtcBytesToCode } from './dtc';

class UdsError extends Error {
  constructor(nrc) {
    super(`NRC 0x${nrc.toString(16)}`);
    this.nrc = nrc;
  }
}

export function createInitialEcuState(profile, now = Date.now()) {
  const didValues = {};
  profile.dids.forEach((d) => {
    if (!d.computed) didValues[d.id] = encodeDidValue(d.defaultValue, d.format, d.length);
  });
  return {
    session: 'default',
    securityUnlocked: false,
    pendingSeed: null,
    failedAttempts: 0,
    lockoutUntil: null,
    lastActivityMs: now,
    didValues,
    dtcs: profile.dtcs.map((d) => ({ ...d, status: d.initialStatus })),
  };
}

function inExtendedOrProgramming(state) {
  return state.session === 'extended' || state.session === 'programming';
}

function requireLength(bytes, expected) {
  const ok = Array.isArray(expected) ? expected.includes(bytes.length) : bytes.length === expected;
  if (!ok) throw new UdsError(0x13);
}

function readDidBytes(profile, state, didId) {
  const entry = profile.dids.find((d) => d.id === didId);
  if (!entry) return null;
  if (entry.computed === 'activeSession') return [SESSION_KEY_TO_ID[state.session]];
  return state.didValues[didId] || null;
}

function computeSecurityKey(seed, algorithm) {
  const seedNum = (seed[0] << 8) | seed[1];
  const result = algorithm.type === 'add'
    ? (seedNum + algorithm.value) & 0xffff
    : (seedNum ^ algorithm.value) & 0xffff;
  return [(result >> 8) & 0xff, result & 0xff];
}

function randomSeed(rng) {
  const r = Math.floor(rng() * 0xfffe) + 1; // never 0000 — that value is reserved to mean "already unlocked"
  return [(r >> 8) & 0xff, r & 0xff];
}

// --- Per-service handlers -------------------------------------------------
// Each returns the positive response byte array, or throws UdsError(nrc). State
// mutations are applied on a shallow-cloned draft the caller commits after a
// handler returns successfully (so a thrown NRC never partially mutates state).

function handleSessionControl(profile, state, bytes, draft) {
  requireLength(bytes, 2);
  const subFn = bytes[1];
  const target = SESSIONS[subFn];
  if (!target) throw new UdsError(0x12);
  const wasNonDefault = state.session !== 'default';
  draft.session = target.key;
  if (target.key === 'default' && wasNonDefault) {
    draft.securityUnlocked = false;
    draft.pendingSeed = null;
  }
  return [0x50, subFn, 0x00, 0x32, 0x01, 0xf4]; // P2/P2* timing bytes are illustrative fixed values
}

function handleEcuReset(profile, state, bytes, draft) {
  requireLength(bytes, 2);
  const subFn = bytes[1];
  if (subFn !== 0x01 && subFn !== 0x03) throw new UdsError(0x12);
  draft.session = 'default';
  draft.securityUnlocked = false;
  draft.pendingSeed = null;
  draft.failedAttempts = 0;
  draft.lockoutUntil = null;
  // DTC memory is non-volatile on a real ECU — a reset does not clear it.
  return [0x51, subFn];
}

function handleClearDiagnosticInformation(profile, state, bytes, draft) {
  requireLength(bytes, 4);
  const group = (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  if (group !== 0xffffff) throw new UdsError(0x31); // only the "all DTCs" group is supported by this simulator
  draft.dtcs = state.dtcs.map((d) => ({ ...d, status: 0x00 }));
  return [0x54];
}

function handleReadDtcInformation(profile, state, bytes, draft) {
  const subFn = bytes[1];
  if (subFn === 0x02) {
    requireLength(bytes, 3);
    const mask = bytes[2];
    const matching = state.dtcs.filter((d) => (d.status & mask) !== 0);
    const availabilityMask = state.dtcs.reduce((acc, d) => acc | d.status, 0);
    const rows = matching.flatMap((d) => [...d.bytes, d.status]);
    return [0x59, 0x02, availabilityMask, ...rows];
  }
  if (subFn === 0x04) {
    requireLength(bytes, 5);
    const dtcBytes = bytes.slice(2, 5);
    const recordNumber = bytes[5] ?? 0x01;
    const entry = state.dtcs.find((d) => d.bytes.every((b, i) => b === dtcBytes[i]));
    if (!entry || !entry.snapshot || entry.snapshot.value == null) throw new UdsError(0x31);
    return [0x59, 0x04, ...entry.bytes, entry.status, recordNumber, 0x01, ...did16ToBytes(entry.snapshot.did), ...entry.snapshot.value];
  }
  throw new UdsError(0x12);
}

function handleReadDataByIdentifier(profile, state, bytes, draft) {
  requireLength(bytes, 3);
  const did = (bytes[1] << 8) | bytes[2];
  const value = readDidBytes(profile, state, did);
  if (value === null) throw new UdsError(0x31);
  return [0x62, bytes[1], bytes[2], ...value];
}

function handleWriteDataByIdentifier(profile, state, bytes, draft) {
  if (bytes.length < 3) throw new UdsError(0x13);
  if (!inExtendedOrProgramming(state)) throw new UdsError(0x22);
  const did = (bytes[1] << 8) | bytes[2];
  const entry = profile.dids.find((d) => d.id === did);
  if (!entry || entry.computed) throw new UdsError(0x31);
  if (!entry.writable) throw new UdsError(0x31);
  if (!state.securityUnlocked) throw new UdsError(0x33);
  const data = bytes.slice(3);
  if (data.length !== entry.length) throw new UdsError(0x13);
  draft.didValues = { ...state.didValues, [did]: data };
  return [0x6e, bytes[1], bytes[2]];
}

function handleSecurityAccess(profile, state, bytes, draft, now) {
  const subFn = bytes[1];
  if (state.lockoutUntil && now < state.lockoutUntil) throw new UdsError(0x37);
  if (state.lockoutUntil && now >= state.lockoutUntil) {
    draft.lockoutUntil = null;
    draft.failedAttempts = 0;
  }
  if (!inExtendedOrProgramming(state)) throw new UdsError(0x22);

  if (subFn === 0x01) {
    requireLength(bytes, 2);
    if (state.securityUnlocked) return [0x67, 0x01, 0x00, 0x00];
    draft.pendingSeed = randomSeed(draft.rng);
    return [0x67, 0x01, ...draft.pendingSeed];
  }
  if (subFn === 0x02) {
    requireLength(bytes, 4);
    if (!state.pendingSeed) throw new UdsError(0x24);
    const sentKey = bytes.slice(2, 4);
    const expectedKey = computeSecurityKey(state.pendingSeed, profile.security);
    draft.pendingSeed = null;
    if (sentKey[0] === expectedKey[0] && sentKey[1] === expectedKey[1]) {
      draft.securityUnlocked = true;
      draft.failedAttempts = 0;
      return [0x67, 0x02];
    }
    const attempts = state.failedAttempts + 1;
    draft.failedAttempts = attempts;
    if (attempts >= (profile.timing?.securityMaxAttempts ?? 3)) {
      draft.lockoutUntil = now + (profile.timing?.securityLockoutMs ?? 30000);
      throw new UdsError(0x36);
    }
    throw new UdsError(0x35);
  }
  throw new UdsError(0x12);
}

function handleTesterPresent(profile, state, bytes, draft) {
  requireLength(bytes, 2);
  const subFn = bytes[1] & 0x7f;
  if (subFn !== 0x00) throw new UdsError(0x12);
  const suppress = (bytes[1] & 0x80) !== 0;
  return suppress ? null : [0x7e, 0x00];
}

const HANDLERS = {
  0x10: handleSessionControl,
  0x11: handleEcuReset,
  0x14: handleClearDiagnosticInformation,
  0x19: handleReadDtcInformation,
  0x22: handleReadDataByIdentifier,
  0x27: handleSecurityAccess,
  0x2e: handleWriteDataByIdentifier,
  0x3e: handleTesterPresent,
};

// Processes one tester request against the current ECU state.
// `rng` (defaults to Math.random) is injectable so scenarios/tests can force a
// deterministic seed.
export function processRequest(profile, state, requestBytes, { now = Date.now(), rng = Math.random } = {}) {
  const sid = requestBytes[0];
  const draft = { ...state, rng };
  draft.lastActivityMs = now;

  if (!HANDLERS[sid]) {
    delete draft.rng;
    return { nextState: draft, responseBytes: [0x7f, sid, 0x11] };
  }
  try {
    const responseBytes = HANDLERS[sid](profile, state, requestBytes, draft, now);
    delete draft.rng;
    return { nextState: draft, responseBytes };
  } catch (e) {
    if (e instanceof UdsError) {
      delete draft.rng;
      return { nextState: draft, responseBytes: [0x7f, sid, e.nrc] };
    }
    throw e;
  }
}

// Called on a UI-driven interval so time-based ECU behavior (the S3 session
// timeout reverting to Default Session) can happen even without a new tester
// request — exactly like a real bench setup left idle mid-session.
export function tickEcu(profile, state, now = Date.now()) {
  if (state.lockoutUntil && now >= state.lockoutUntil) {
    return {
      nextState: { ...state, lockoutUntil: null, failedAttempts: 0 },
      event: { type: 'lockout-expired', message: 'Security Access lockout delay has expired — Security Access attempts may resume.' },
    };
  }
  if (state.session === 'default') return { nextState: state, event: null };
  const s3Ms = profile.timing?.s3Ms ?? 20000;
  if (now - state.lastActivityMs > s3Ms) {
    return {
      nextState: { ...state, session: 'default', securityUnlocked: false, pendingSeed: null },
      event: { type: 's3-timeout', message: 'S3 session timer expired with no Tester Present — ECU reverted to Default Session.' },
    };
  }
  return { nextState: state, event: null };
}

// Practitioner-mode "occasional realistic fault" injection. Only ever called
// when the user has explicitly enabled fault injection — Learner mode never
// calls this, keeping its responses deterministic per the spec.
export function maybeInjectFault(profile, state, now, rng = Math.random) {
  if (rng() < 0.985) return { nextState: state, event: null };

  if (state.securityUnlocked && rng() < 0.5) {
    return {
      nextState: { ...state, securityUnlocked: false },
      event: { type: 'fault-relock', message: 'Simulated transient fault: the ECU re-locked Security Access on its own (e.g. a supply voltage dip resetting the security state machine).' },
    };
  }
  const reArmable = state.dtcs.find((d) => d.faultActive && d.status === 0x00);
  if (reArmable) {
    return {
      nextState: {
        ...state,
        dtcs: state.dtcs.map((d) => (d === reArmable ? { ...d, status: 0x29 } : d)),
      },
      event: { type: 'fault-rearm', message: `Simulated recurring fault: ${dtcBytesToCode(reArmable.bytes)} reappeared — the underlying fault condition is still physically present, so the ECU re-detected and re-confirmed it.` },
    };
  }
  return { nextState: state, event: null };
}
